<script setup>
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler.js";
import { useClientRequests } from "../../Composables/useClientRequests.js";
import { useVehicleConfigurator } from "../../Composables/useVehicleConfigurator.js";
import { useRoute, useRouter } from "vue-router";
import { onBeforeMount, reactive, ref } from "vue";
import Loader from "../../Components/Loader.vue";
import FormSelectField from "../../Components/FormSelectField.vue";
import FormSelectCenter from "../../Components/FormSelectCenter.vue";
import FormTextarea from "../../Components/FormTextarea.vue";
import FormSelectClient from "../../Components/FormSelectClient.vue";
import FormNumericField from "../../Components/FormNumericField.vue";
import FormTextField from "../../Components/FormTextField.vue";
import FormMultiSelect from "../../Components/FormMultiSelect.vue";
import { userIsAdmin, userIsBackOffice } from "../../Utils/userRoleUtils.js";
import FormVehicleConfigurator from "../../Components/FormVehicleConfigurator.vue";
import FormDatePicker from "../../Components/FormDatePicker.vue";

const { httpErrorsHandler } = useHttpErrorsHandler();
const route = useRoute();
const router = useRouter();

const form = reactive({
    client_id: null,
    center_id: null,
    status: null,
    reason_for_loss: null,
    tags: [],
    vehicle_brand_id: null,
    vehicle_model_id: null,
    vehicle_version_id: null,
    vehicle_engine_id: null,
    car_stage: null,
    contact_reason: null,
    vehicle_type: null,
    notes: null,
    request_origin: null,
    vin: null,
    license_plate: null,
    vehicle_year: null,
    mileage: null,
    spark_plug_mileage: null,
    coil_mileage: null,
    octane_rating: null,
    price: null,
    currency: null
})
const centerPreSelected = ref([])
const clientPreSelected = ref([])
const tagsPreSelected = ref([])

const clientRequestId = route.params.id

const {
    brands: carBrands,
    models: carModels,
    versions: carVersions,
    engines: carEngines,
    initializeFromData,
    setInitialized,
} = useVehicleConfigurator(form, {
    onError: httpErrorsHandler,
})

const { updating, fetching, errors, updateClientRequest, fetchClientRequest } = useClientRequests({
    onFetched: async (data) => {
        await initializeFromData(data)

        Object.entries(data).forEach(([key, value]) => {
            if (key === 'center') {
                centerPreSelected.value = value;
                return;
            }
            if (key === 'client') {
                clientPreSelected.value = value;
                return;
            }
            if (key === 'tags') {
                tagsPreSelected.value = value;
                return;
            }
            if (Object.hasOwn(form, key) && key !== 'address') {
                form[key] = value;
            }
        });
        setInitialized();
    },
    onUpdated: () => router.back(),
    onError: (error) => httpErrorsHandler(error),
})

onBeforeMount(() => {
    fetchClientRequest(clientRequestId)
})

const handleSubmit = () => updateClientRequest(clientRequestId, form)

</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <!--Loader-->
            <div class="card" v-if="fetching || updating">
                <div class="card-body">
                    <loader/>
                </div>
            </div>
        </div>
    </div>
    <!--Form-->
    <form v-on:submit.prevent="handleSubmit" v-show="!fetching && !updating">
        <div class="row">
            <div class="col-lg-6">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-information-outline me-2 align-middle fs-4"></i>Informations demande</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">

                            <form-select-client
                                :errors="errors"
                                label="Client"
                                name="client_id"
                                :required="true"
                                :data="form.client_id"
                                @update:field="form.client_id = ($event !== null ? $event.id : null)"
                                bs-class="col-xl-6"
                                :disabled="false"
                                :taggable="false"
                                :label-hidden="false"
                                placeholder="Sélectionnez un client"
                                :client-pre-selected="clientPreSelected"
                            />


                            <form-select-center
                                :errors="errors"
                                label="Centre"
                                name="center_id"
                                :required="true"
                                :data="form.center_id"
                                @update:field="form.center_id = ($event !== null ? $event.id : null)"
                                bs-class="col-xl-6"
                                :disabled="false"
                                :taggable="false"
                                :label-hidden="false"
                                placeholder="Sélectionnez un centre"
                                :center-pre-selected="centerPreSelected"
                            />

                            <form-select-field
                                name="status_request"
                                label="Statut"
                                :label-hidden="false"
                                @update:field="form.status = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.status"
                                bs-class="col-xl-6"
                                :liste-options="[
                                    { text: 'À traiter', value: 'pending' },
                                    { text: 'Répondu', value: 'responded' },
                                    { text: 'Pièces commandées', value: 'parts_ordered' },
                                    { text: 'RDV programmé', value: 'appointment_scheduled' },
                                    { text: 'RDV en cours', value: 'appointment_ongoing' },
                                    { text: 'Gagné', value: 'won' },
                                    { text: 'Client non présenté', value: 'no_show' },
                                    { text: 'Perdu', value: 'lost' },
                                    { text: 'Fermé', value: 'closed' }
                                ]"
                            />

                            <form-select-field
                                name="request_origin"
                                label="Origine demande"
                                :label-hidden="false"
                                @update:field="form.request_origin = $event"
                                :errors="errors"
                                :required="true"
                                :data="form.request_origin"
                                bs-class="col-xl-6"
                                :disabled="false"
                                :liste-options="[
                                    { text: 'Comptoir', value: 'Comptoir' },
                                    { text: 'Événement', value: 'Événement' },
                                    { text: 'Influenceur', value: 'Influenceur' },
                                    { text: 'Réseaux sociaux', value: 'Réseaux sociaux' },
                                    { text: 'RingOver', value: 'RingOver' },
                                    { text: 'ADS', value: 'ADS' },
                                    { text: 'Site web', value: 'Site web' },
                                    { text: 'Site web - ChatBot', value: 'Site web - ChatBot' },
                                    { text: 'Campagne SMS', value: 'Campagne SMS' },
                                    { text: 'Campagne Mail', value: 'Campagne Mail' },
                                    { text: 'Parrainage', value: 'Parrainage' },
                                    { text: 'Landing page', value: 'Landing page' },
                                ]"
                            />

                            <form-select-field
                                v-show="form.status === 'lost'"
                                name="reason_for_loss"
                                label="Raison de la perte"
                                :label-hidden="false"
                                @update:field="form.reason_for_loss = $event"
                                :errors="errors"
                                :required="form.status === 'lost'"
                                :data="form.reason_for_loss"
                                bs-class="col-xl-12"
                                :disabled="false"
                                :liste-options="[
                                    { text: 'Doublon', value: 'Doublon' },
                                    { text: 'Plus de réponse', value: 'Plus de réponse' },
                                    { text: 'Prix', value: 'Prix' },
                                    { text: 'Concurrent', value: 'Concurrent' },
                                    { text: 'Délais', value: 'Délais' },
                                    { text: 'Perte de confiance', value: 'Perte de confiance' },
                                    { text: 'Hors sujet', value: 'Hors sujet' },
                                    { text: 'Garantie', value: 'Garantie' },
                                    { text: 'Assurance', value: 'Assurance' },
                                ]"
                            />

                            <form-select-field
                                name="contact_reason"
                                label="Raison contact"
                                :label-hidden="false"
                                @update:field="form.contact_reason = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.contact_reason"
                                bs-class="col-xl-6"
                                :liste-options="[
                                    { text: 'Rendez-vous', value: 'Rendez-vous' },
                                    { text: 'Demande de rappel', value: 'Demande de rappel' },
                                    { text: 'Devis', value: 'Devis' },
                                    { text: 'Demande d’information', value: 'Demande d’information' },
                                    { text: 'Devenir franchisé', value: 'Devenir franchisé' },
                                    { text: 'Reprog. en Attente', value: 'Reprog. en Attente' },
                                    { text: 'Participer au Dev.', value: 'Participer au Dev.' },
                                    { text: 'Assistance', value: 'Assistance' },
                                    { text: 'JOBS', value: 'JOBS' },
                                    { text: 'Tournage vidéo Youtube', value: 'Tournage vidéo Youtube' },
                                    { text: 'Autre', value: 'Autre' }
                                ]"
                            />

                            <form-select-field
                                name="car_stage"
                                label="Prestation souhaitée"
                                :label-hidden="false"
                                @update:field="form.car_stage = $event"
                                :errors="errors"
                                :required="!userIsAdmin() && !userIsBackOffice()"
                                :data="form.car_stage"
                                bs-class="col-xl-6"
                                :liste-options="[
                                    { text: 'Reprogrammation stage 0', value: 'Reprogrammation stage 0' },
                                    { text: 'Reprogrammation stage 1', value: 'Reprogrammation stage 1' },
                                    { text: 'Reprogrammation stage 2', value: 'Reprogrammation stage 2' },
                                    { text: 'Reprogrammation stage 3', value: 'Reprogrammation stage 3' },
                                    { text: 'Boîte de vitesses', value: 'Boîte de vitesses' },
                                    { text: 'Conversion E85', value: 'Conversion E85' },
                                    { text: 'Conversion E85+', value: 'Conversion E85+' },
                                    { text: 'Pièces performance', value: 'Pièces performance' },
                                    { text: 'Entretien', value: 'Entretien' },
                                    { text: 'Passage au banc', value: 'Passage au banc' },
                                    { text: 'Esthétique', value: 'Esthétique' },
                                    { text: 'Diagnostic', value: 'Diagnostic' },
                                ]"
                            />

                            <form-numeric-field
                                name="price"
                                label="Prix"
                                :label-hidden="false"
                                @update:field="form.price = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.price"
                                bs-class="col-md-6"
                                :min="0"
                                :step="0.01"
                            />

                            <form-select-field
                                name="currency"
                                label="Devise"
                                :label-hidden="false"
                                @update:field="form.currency = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.currency"
                                bs-class="col-md-6"
                                :liste-options="[
                                    { text: 'EUR', value: 'EUR' },
                                ]"
                            />

                            <form-multi-select
                                :errors="errors"
                                label="Tags"
                                name="tags"
                                :required="false"
                                :data="form.tags"
                                @update:field="form.tags = $event"
                                bs-class="col-md-12"
                                :disabled="false"
                                :label-hidden="false"
                                placeholder="Sélectionnez un ou plusieurs tags"
                                :options-pre-selected="tagsPreSelected"
                                :liste-options="[
                                    { name: 'Promotion', id: 'Promotion' },
                                    { name: 'ECU en dev', id: 'ECU en dev' },
                                    { name: 'SAV', id: 'SAV' },
                                    { name: 'Remise Prépa', id: 'Remise Prépa' },
                                    { name: 'Remise origine', id: 'Remise origine' },
                                    { name: 'Franchise', id: 'Franchise' },
                                    { name: 'Jobs', id: 'Jobs' },
                                    { name: 'Jeux-concours', id: 'Jeux-concours' },
                                    { name: 'Doublon', id: 'Doublon' },
                                    { name: 'Tournage vidéo Youtube', id: 'Tournage vidéo Youtube' }
                                ]"
                            />

                            <form-textarea
                                name="notes"
                                label="Notes"
                                :label-hidden="false"
                                @update:field="form.notes = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.notes"
                                bs-class="col-xl-12"
                                :row="2"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-lg-6">
                <!--Vehicle-->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-car me-2 align-middle fs-4"></i>Véhicule concerné</h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <form-vehicle-configurator
                                :form="form"
                                :errors="errors"
                                :brands="carBrands"
                                :models="carModels"
                                :versions="carVersions"
                                :engines="carEngines"
                            />

                            <form-text-field
                                name="vin"
                                label="VIN"
                                :label-hidden="false"
                                @update:field="form.vin = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.vin"
                                bs-class="col-md-6"
                                :show-char-count="true"
                                :max-length="17"
                            />

                            <form-text-field
                                name="license_plate"
                                label="Immatriculation"
                                :label-hidden="false"
                                @update:field="form.license_plate = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.license_plate"
                                bs-class="col-md-6"
                            />

                            <form-date-picker
                                name="vehicle_year"
                                label="Date de première immatriculation"
                                :label-hidden="false"
                                @update:field="form.vehicle_year = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.vehicle_year"
                                bs-class="col-md-6"
                                :disabled="false"
                            />

                            <!--<form-numeric-field-->
                            <!--    name="vehicle_year"-->
                            <!--    label="Année du véhicule"-->
                            <!--    :label-hidden="false"-->
                            <!--    @update:field="form.vehicle_year = $event"-->
                            <!--    :errors="errors"-->
                            <!--    :required="false"-->
                            <!--    :data="form.vehicle_year"-->
                            <!--    bs-class="col-md-6"-->
                            <!--    :step="1"-->
                            <!--    :min="1900"-->
                            <!--    :max="new Date().getFullYear()"-->
                            <!--/>-->

                            <form-numeric-field
                                name="mileage"
                                label="Kilométrage"
                                :label-hidden="false"
                                @update:field="form.mileage = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.mileage"
                                bs-class="col-md-6"
                                :step="1"
                                :min="0"
                            />

                            <form-select-field
                                name="spark_plug_mileage"
                                label="Kilométrage des bougies"
                                :label-hidden="false"
                                @update:field="form.spark_plug_mileage = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.spark_plug_mileage"
                                bs-class="col-xl-6"
                                :liste-options="[
                                    { text: 'Moins de 20 000 km', value: 'Moins de 20 000 km' },
                                    { text: 'Entre 20 et 50 000 km', value: 'Entre 20 et 50 000 km' },
                                    { text: 'Plus de 50 000 km', value: 'Plus de 50 000 km' },
                                ]"
                                information-content="Donnée utile pour un moteur essence"
                            />

                            <form-select-field
                                name="coil_mileage"
                                label="Kilométrage des bobines"
                                :label-hidden="false"
                                @update:field="form.coil_mileage = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.coil_mileage"
                                bs-class="col-xl-6"
                                :liste-options="[
                                    { text: 'Moins de 20 000 km', value: 'Moins de 20 000 km' },
                                    { text: 'Entre 20 et 50 000 km', value: 'Entre 20 et 50 000 km' },
                                    { text: 'Plus de 50 000 km', value: 'Plus de 50 000 km' },
                                    { text: 'Je ne sais pas', value: 'Je ne sais pas' },
                                ]"
                                information-content="Donnée utile pour un moteur essence"
                            />

                            <form-select-field
                                name="octane_rating"
                                label="Indice Octane"
                                :label-hidden="false"
                                @update:field="form.octane_rating = $event"
                                :errors="errors"
                                :required="false"
                                :data="form.octane_rating"
                                bs-class="col-xl-12"
                                :liste-options="[
                                    { text: 'SP95', value: 'SP95' },
                                    { text: 'SP98', value: 'SP98' },
                                    { text: 'SP100/102', value: 'SP100/102' },
                                    { text: 'E85', value: 'E85' },
                                    { text: 'E85/SP95 (Mix)', value: 'E85/SP95 (Mix)' },
                                ]"
                                information-content="Donnée utile pour un moteur essence"
                            />

                            <div class="d-flex gap-2 justify-content-end">
                                <button type="button" class="btn btn-soft-dark" @click="router.back()">Retour</button>
                                <button type="submit" class="btn btn-secondary">Valider</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </form>
</template>

<style scoped>

</style>
